import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { CheckCircle2, Lock, Trophy } from "lucide-react";
import NinjaBadge from "@/components/NinjaBadge";

export default function Profile() {
  const { user, rank } = useAuth();
  const [levels, setLevels] = useState([]);

  useEffect(() => {
    api.get("/levels").then(({ data }) => setLevels(data.levels));
  }, []);

  const completed = user?.completed_levels || [];
  const scores = user?.quiz_scores || {};

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="flex items-center gap-6 reveal">
          <NinjaBadge level={Math.max(1, completed.length ? Math.max(...completed) : 1)} size={112} glow />
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-green-400 mb-1">
              Ninja Profile
            </div>
            <h1 className="font-display text-4xl font-bold" data-testid="profile-name">
              {user?.name || "Anonymous Ninja"}
            </h1>
            <div className="text-slate-400 text-sm mt-1">{user?.email}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-10">
          <StatCard label="Rank" value={rank || "Student Ninja"} testid="profile-rank" />
          <StatCard label="Total XP" value={user?.xp || 0} testid="profile-xp" />
          <StatCard label="Belts Earned" value={`${completed.length} / 8`} testid="profile-belts" />
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-4">
          <StatCard label="Code Blue High" value={user?.code_blue_high_score || 0} testid="profile-code-blue-high" />
          <StatCard label="Best Streak" value={`${user?.code_blue_best_rounds || 0} rounds`} testid="profile-code-blue-rounds" />
          <StatCard label="Arcade Plays" value={user?.code_blue_plays || 0} testid="profile-code-blue-plays" />
        </div>

        <div className="mt-14">
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4">
            Belt Progress
          </div>
          <div className="space-y-2">
            {levels.map((lvl) => {
              const isDone = completed.includes(lvl.id);
              const highest = completed.length ? Math.max(...completed) : 0;
              const isLocked = lvl.id > highest + 1;
              const score = scores[lvl.id];
              return (
                <div
                  key={lvl.id}
                  className={`flex items-center justify-between p-4 rounded-md border ${
                    isDone
                      ? "border-green-500/30 bg-green-500/5"
                      : isLocked
                      ? "border-white/5 bg-white/[0.02] opacity-60"
                      : "border-white/10 bg-white/[0.03]"
                  }`}
                  data-testid={`profile-level-${lvl.id}`}
                >
                  <div className="flex items-center gap-4">
                    <NinjaBadge level={lvl.id} size={44} locked={isLocked} />
                    <span className="font-mono text-xs text-slate-500 w-10">
                      LVL {String(lvl.id).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="font-display font-semibold">{lvl.rank}</div>
                      <div className="text-xs text-slate-500">{lvl.tagline}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {score !== undefined && (
                      <span className="font-mono text-xs text-slate-400">{score}%</span>
                    )}
                    {isDone ? (
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                    ) : isLocked ? (
                      <Lock className="w-5 h-5 text-slate-600" />
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-red-400 px-2 py-0.5 rounded-full border border-red-500/40 bg-red-500/10">
                        Active
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="p-5 rounded-lg border border-white/10 bg-white/[0.03]" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">
        {label}
      </div>
      <div className="font-display text-2xl font-bold">{value}</div>
    </div>
  );
}
